import { FC, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  CardMedia,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Done as DoneIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import {
  useGetProjectQuery,
  useGetClientQuery,
  useUpdateProjectMutation,
  useUploadProjectImageMutation,
  useDeleteProjectImageMutation,
} from '../../redux/hooks.ts'
import { Layout } from '../../components/layout.tsx'
import { MainContent } from '../../components/main-content.tsx'
import { MainContentHeader } from '../../components/main-content-header.tsx'
import { MainContentBody } from '../../components/main-content-body.tsx'
import { ProjectFormModal, ProjectItemsTable } from '../../components/index.ts'
import type { ProjectUpdate } from '@itemize/server/models'

export const ClientProjectPage: FC = () => {
  const { clientId, projectId } = useParams<{ clientId: string; projectId: string }>()
  const navigate = useNavigate()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    data: project,
    isLoading: projectLoading,
    error: projectError,
  } = useGetProjectQuery(projectId!)
  const {
    data: client,
    isLoading: clientLoading,
    error: clientError,
  } = useGetClientQuery(clientId!)
  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation()
  const [uploadProjectImage, { isLoading: isUploading }] = useUploadProjectImageMutation()
  const [deleteProjectImage, { isLoading: isDeleting }] = useDeleteProjectImageMutation()

  // Early returns after all hooks are declared
  if (projectLoading || clientLoading) return <Typography>Loading...</Typography>
  if (projectError || clientError) return <Typography>Error loading data</Typography>
  if (!project || !client) return <Typography>Project or client not found</Typography>

  // Verify that the project belongs to the client
  if (project.clientId !== clientId) {
    return <Typography>Project does not belong to this client</Typography>
  }

  const handleBackToClient = () => {
    navigate(`/clients/${clientId}`)
  }

  const handleClientNameClick = () => {
    navigate(`/clients/${clientId}`)
  }

  const handleEdit = () => {
    setIsEditModalOpen(true)
  }

  const handleEditSubmit = async (data: ProjectUpdate) => {
    await updateProject({
      id: project.id,
      data,
    }).unwrap()
  }

  const handleEditClose = () => {
    setIsEditModalOpen(false)
  }

  const handleCloseProject = async () => {
    await updateProject({
      id: project.id,
      data: { status: 'closed' },
    }).unwrap()
    navigate('/projects')
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type.startsWith('image/') && project?.id) {
      uploadProjectImage({ projectId: project.id, image: file })
    }
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)
    const file = event.dataTransfer.files[0]
    if (file && file.type.startsWith('image/') && project?.id) {
      uploadProjectImage({ projectId: project.id, image: file })
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleRemoveImage = () => {
    if (project?.id) {
      deleteProjectImage(project.id)
    }
  }

  const handleImageClick = () => {
    fileInputRef.current?.click()
  }

  const BreadcrumbTitle = () => (
    <Box display="flex" alignItems="center" gap={1}>
      <Button
        onClick={handleClientNameClick}
        variant="text"
        sx={{
          padding: 0,
          minWidth: 'auto',
          textTransform: 'none',
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'primary.main',
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
          },
        }}
      >
        {client.name}
      </Button>
      <ArrowForwardIosIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      <Typography variant="h5" component="span" fontWeight="600" color="text.primary">
        {project.name}
      </Typography>
    </Box>
  )

  return (
    <Layout>
      <MainContent>
        <MainContentHeader
          title={<BreadcrumbTitle />}
          breadcrumbs={[
            { label: client.name, href: `/clients/${clientId}` },
            { label: project.name, href: `/clients/${clientId}/projects/${projectId}` },
          ]}
          buttons={
            <>
              <Button startIcon={<ArrowBackIcon />} onClick={handleBackToClient} variant="outlined">
                Back to Client
              </Button>
              <Button startIcon={<EditIcon />} onClick={handleEdit} variant="contained">
                Edit
              </Button>
              {project.status === 'open' && (
                <Button
                  startIcon={<DoneIcon />}
                  onClick={handleCloseProject}
                  variant="contained"
                  color="success"
                >
                  Mark as Done
                </Button>
              )}
            </>
          }
        />
        <MainContentBody>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }} component="div">
              <Card>
                <Box display="flex" flexDirection="row">
                  <Box
                    px={4}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <Box
                      onClick={handleImageClick}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: '1em',
                        overflow: 'hidden',
                        border: isDragOver ? '2px dashed #1976d2' : '2px dashed transparent',
                        transition: 'border-color 0.2s ease',
                        '&:hover': {
                          border: '2px dashed #1976d2',
                          '& .upload-overlay': {
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={project.imageUrl || '/images/image-placeholder.jpg'}
                        alt={project.name}
                        sx={{
                          objectFit: 'contain',
                          borderRadius: '1em',
                        }}
                      />
                      <Box
                        className="upload-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                          borderRadius: '1em',
                        }}
                      >
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                          <CloudUploadIcon sx={{ color: 'white', fontSize: 32 }} />
                          <Typography variant="body2" color="white" textAlign="center">
                            {isDragOver ? 'Drop image here' : 'Click to upload or drag & drop'}
                          </Typography>
                        </Box>
                      </Box>
                      {project.imageUrl && (
                        <Tooltip title="Remove image">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRemoveImage()
                            }}
                            disabled={isDeleting}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              },
                            }}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                    />
                  </Box>
                  <CardContent sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <WorkIcon color="primary" />
                      <Typography variant="h6">Project Information</Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Project Name
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">{project.name}</Typography>
                          {/* <Chip
                          label={project.status === 'open' ? 'Open' : 'Closed'}
                          size="small"
                          variant="outlined"
                          color={project.status === 'open' ? 'success' : 'secondary'}
                        /> */}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Description
                        </Typography>
                        <Typography variant="body1">
                          {project.description || 'No description provided'}
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="row" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Created
                          </Typography>
                          <Typography variant="body1">
                            {new Date(project.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Last Updated
                          </Typography>
                          <Typography variant="body1">
                            {new Date(project.updatedAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Status
                          </Typography>
                          <Chip
                            label={project.status === 'open' ? 'Open' : 'Closed'}
                            size="small"
                            variant="outlined"
                            color={project.status === 'open' ? 'success' : 'secondary'}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Box>
              </Card>
            </Grid>

            <Grid size={{ xs: 12 }} component="div">
              <ProjectItemsTable project={project} client={client} />
            </Grid>
          </Grid>
        </MainContentBody>
      </MainContent>

      <ProjectFormModal
        open={isEditModalOpen}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        initialData={{
          name: project.name,
          clientId: project.clientId,
          description: project.description || '',
        }}
        title="Edit Project"
        submitText="Update Project"
        isLoading={isUpdating}
      />
    </Layout>
  )
}
